import React, { useState, useEffect } from 'react';
import { BookOpen, MessageSquare, Wrench, Lightbulb, X, ChevronRight } from 'lucide-react';
import FAQAccordionItem from './FAQAccordionItem';

interface FAQSectionProps {
  language: string;
}

const FAQSection: React.FC<FAQSectionProps> = ({ language }) => {
  const [openGeneralGuide, setOpenGeneralGuide] = useState<number>(0);
  const [openFAQ, setOpenFAQ] = useState<number>(0);
  const [openTechnicalSupport, setOpenTechnicalSupport] = useState<number>(0);
  
  const [tourStep, setTourStep] = useState<number>(0); // 0 = inactive, 1 = general, 2 = faq, 3 = technical

  const handleNextTour = () => {
    if (tourStep === 1) setOpenGeneralGuide(0);
    if (tourStep === 2) setOpenFAQ(0);
    if (tourStep === 3) setOpenTechnicalSupport(0);
    
    if (tourStep >= 3) {
      setTourStep(0); // end tour
      document.body.style.overflow = 'auto';
    } else {
      setTourStep(prev => prev + 1);
    }
  };

  const startTour = () => {
    setTourStep(1);
    setOpenGeneralGuide(-1);
    setOpenFAQ(-1);
    setOpenTechnicalSupport(-1);
    document.body.style.overflow = 'hidden';
  };
  
  const stopTour = () => {
    setTourStep(0);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    if (tourStep > 0) {
      setTimeout(() => {
        const element = document.getElementById('tour-step-' + tourStep);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  }, [tourStep]);

  const translations = {
    en: {
      generalGuide: 'General Guide',
      faq: 'Frequently Asked Questions',
      technicalSupport: 'Technical Support',
      generalGuideItems: [
        {
          question: 'How to navigate the website?',
          answer: 'Use the navigation menu at the top to quickly access different sections: Home, About, Skills, Projects, and Contact. You can also scroll smoothly through each section.'
        },
        {
          question: 'How to contact me?',
          answer: <>Scroll to the Contact section at the bottom of the page. You can reach me via email at <i>ntainguyen.it@gmail.com</i>, phone at <i>(+84) 366 063 944</i>, or through my social media profiles.</>
        },
        {
          question: 'How to view my projects?',
          answer: 'Visit the Projects section to see my portfolio. Each project card includes details, technologies used, and links to live demos or source code repositories.'
        }
      ],
      faqItems: [
        {
          question: 'What technologies do you specialize in?',
          answer: 'I specialize in Backend Architecture and Software Engineering using modern technologies like C# and .NET.'
        },
        {
          question: 'Are you available for freelance work?',
          answer: 'Yes! I am currently available for freelance opportunities. Feel free to contact me through the contact section to discuss your project requirements.'
        },
        {
          question: 'Can I download your resume?',
          answer: 'You can find detailed information about my education, skills, and experience throughout this website. For a formal resume, please contact me directly.'
        }
      ],
      technicalSupportItems: [
        {
          question: 'The website is not loading properly',
          answer: 'Try refreshing your browser or clearing your cache. This website works best on modern browsers like Chrome, Firefox, Safari, or Edge. Make sure JavaScript is enabled.'
        },
        {
          question: 'Dark mode is not working',
          answer: 'Check if your browser supports dark mode. You can toggle dark mode using the sun/moon icon in the navigation bar. If issues persist, try updating your browser.'
        },
        {
          question: 'How do I report a bug or issue?',
          answer: <>If you encounter any bugs or issues, please contact me via email at <i>ntainguyen.it@gmail.com</i> with a description of the problem and screenshots if possible.</>
        }
      ]
    },
    vi: {
      generalGuide: 'Hướng dẫn chung',
      faq: 'Câu hỏi thường gặp',
      technicalSupport: 'Hỗ trợ kỹ thuật',
      generalGuideItems: [
        {
          question: 'Làm thế nào để điều hướng trang web?',
          answer: 'Sử dụng menu điều hướng ở trên cùng để truy cập nhanh các phần khác nhau: Trang chủ, Giới thiệu, Kỹ năng, Dự án và Liên hệ. Bạn cũng có thể cuộn qua từng phần một cách mượt mà.'
        },
        {
          question: 'Làm thế nào để liên hệ với mình?',
          answer: <>Cuộn đến phần Liên hệ ở cuối trang. Bạn có thể liên hệ với mình qua email tại <i>ntainguyen.it@gmail.com</i>, điện thoại tại <i>(+84) 366 063 944</i>, hoặc thông qua các hồ sơ mạng xã hội của mình.</>
        },
        {
          question: 'Làm thế nào để xem các dự án của mình?',
          answer: 'Truy cập phần Dự án để xem danh mục đầu tư của mình. Mỗi thẻ dự án bao gồm chi tiết, công nghệ được sử dụng và liên kết đến bản demo trực tiếp hoặc kho mã nguồn.'
        }
      ],
      faqItems: [
        {
          question: 'Bạn chuyên về công nghệ nào?',
          answer: 'Mình chuyên về Kiến trúc Backend và Kỹ thuật Phần mềm sử dụng các công nghệ hiện đại như C# và .NET.'
        },
        {
          question: 'Bạn có sẵn sàng làm việc tự do không?',
          answer: 'Có! Mình hiện đang sẵn sàng cho các cơ hội làm việc tự do. Hãy liên hệ với mình qua phần liên hệ để thảo luận về yêu cầu dự án của bạn.'
        },
        {
          question: 'Tôi có thể tải xuống sơ yếu lý lịch của bạn không?',
          answer: 'Bạn có thể tìm thông tin chi tiết về học vấn, kỹ năng và kinh nghiệm của mình trên trang web này. Để có sơ yếu lý lịch chính thức, vui lòng liên hệ trực tiếp với mình.'
        }
      ],
      technicalSupportItems: [
        {
          question: 'Trang web không tải đúng cách',
          answer: 'Hãy thử làm mới trình duyệt hoặc xóa bộ nhớ cache. Trang web này hoạt động tốt nhất trên các trình duyệt hiện đại như Chrome, Firefox, Safari hoặc Edge. Đảm bảo JavaScript được bật.'
        },
        {
          question: 'Chế độ tối không hoạt động',
          answer: 'Kiểm tra xem trình duyệt của bạn có hỗ trợ chế độ tối không. Bạn có thể chuyển đổi chế độ tối bằng biểu tượng mặt trời/mặt trăng trên thanh điều hướng. Nếu vấn đề vẫn tiếp diễn, hãy thử cập nhật trình duyệt.'
        },
        {
          question: 'Làm thế nào để báo cáo lỗi hoặc vấn đề?',
          answer: <>Nếu bạn gặp bất kỳ lỗi hoặc vấn đề nào, vui lòng liên hệ với mình qua email tại <i>ntainguyen.it@gmail.com</i> với mô tả về vấn đề và ảnh chụp màn hình nếu có thể.</>
        }
      ]
    }
  };

  const t = translations[language as keyof typeof translations];

  return (
    <div id="faq-container" className="mt-16 w-full max-w-4xl mx-auto space-y-12 relative">
      
      {tourStep > 0 && (
        <div className="fixed inset-0 bg-black/70 z-[60] transition-opacity duration-500" onClick={stopTour}></div>
      )}

      {/* General Guide Section */}
      <div id="tour-step-1" className={tourStep === 1 ? 'relative z-[70] bg-white dark:bg-gray-800 p-6 rounded-2xl ring-4 ring-blue-500 transition-all scale-[1.02]' : 'transition-all'}>
        {tourStep === 1 && (
          <div className="absolute -top-16 left-0 bg-blue-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-bounce">
            <span>{language === 'vi' ? 'B1: Tại đây chứa các hướng dẫn chung về website.' : 'Step 1: Here you find general guides.'}</span>
            <button onClick={handleNextTour} className="bg-white text-blue-600 px-3 py-1 rounded-lg text-sm font-bold hover:bg-gray-100">
              {language === 'vi' ? 'Tiếp' : 'Next'} <ChevronRight size={16} className="inline"/>
            </button>
            <button onClick={stopTour} className="text-blue-200 hover:text-white"><X size={20}/></button>
          </div>
        )}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mb-6" data-aos="fade-up">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
              {t.generalGuide}
            </h3>
          </div>
          <button 
            onClick={startTour}
            className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-800 px-3 py-1.5 rounded-lg transition-all text-sm font-medium"
          >
            <Lightbulb size={16} className="animate-pulse" />
            {language === 'vi' ? 'Xem hướng dẫn tương tác' : 'Interactive Guided Tour'}
          </button>
        </div>
        <div className="space-y-4">
          {t.generalGuideItems.map((item, index) => (
            <FAQAccordionItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openGeneralGuide === index}
              onToggle={() => setOpenGeneralGuide(openGeneralGuide === index ? -1 : index)}
              language={language}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div id="tour-step-2" className={tourStep === 2 ? 'relative z-[70] bg-white dark:bg-gray-800 p-6 rounded-2xl ring-4 ring-blue-500 transition-all scale-[1.02]' : 'transition-all'}>
        {tourStep === 2 && (
          <div className="absolute -top-16 left-0 bg-blue-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-bounce">
            <span>{language === 'vi' ? 'B2: Các câu hỏi thường gặp về bản thân mình.' : 'Step 2: Frequently asked questions about me.'}</span>
            <button onClick={handleNextTour} className="bg-white text-blue-600 px-3 py-1 rounded-lg text-sm font-bold hover:bg-gray-100">
              {language === 'vi' ? 'Tiếp' : 'Next'} <ChevronRight size={16} className="inline"/>
            </button>
            <button onClick={stopTour} className="text-blue-200 hover:text-white"><X size={20}/></button>
          </div>
        )}
        <div className="flex items-center gap-3 mb-6" data-aos="fade-up">
          <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
            <MessageSquare className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
            {t.faq}
          </h3>
        </div>
        <div className="space-y-4">
          {t.faqItems.map((item, index) => (
            <FAQAccordionItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openFAQ === index}
              onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              language={language}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Technical Support Section */}
      <div id="tour-step-3" className={tourStep === 3 ? 'relative z-[70] bg-white dark:bg-gray-800 p-6 rounded-2xl ring-4 ring-blue-500 transition-all scale-[1.02]' : 'transition-all'}>
        {tourStep === 3 && (
          <div className="absolute -top-16 left-0 bg-blue-600 text-white px-4 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-bounce">
            <span>{language === 'vi' ? 'B3: Hỗ trợ kỹ thuật. Click nút Loa bên cạnh câu trả lời để nghe nội dung nhé!' : 'Step 3: Tech support. Click the Speaker icon next to an answer to hear it!'}</span>
            <button onClick={handleNextTour} className="bg-white text-blue-600 px-3 py-1 rounded-lg text-sm font-bold hover:bg-gray-100">
              {language === 'vi' ? 'Hoàn thành' : 'Done'} <ChevronRight size={16} className="inline"/>
            </button>
            <button onClick={stopTour} className="text-blue-200 hover:text-white"><X size={20}/></button>
          </div>
        )}
        <div className="flex items-center gap-3 mb-6" data-aos="fade-up">
          <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
            <Wrench className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
            {t.technicalSupport}
          </h3>
        </div>
        <div className="space-y-4">
          {t.technicalSupportItems.map((item, index) => (
            <FAQAccordionItem
              key={index}
              question={item.question}
              answer={item.answer}
              isOpen={openTechnicalSupport === index}
              onToggle={() => setOpenTechnicalSupport(openTechnicalSupport === index ? -1 : index)}
              language={language}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQSection;
